import { createContext, useContext, useCallback, useEffect, useState, type ReactNode } from 'react'
import {
  type AuthUser,
  type TokenResponse,
  clearTokens,
  getStoredUser,
  isAuthenticated,
  saveTokens,
} from './auth'

interface AuthContextValue {
  user: AuthUser | null
  isLoggedIn: boolean
  login: (data: TokenResponse) => void
  logout: () => void
  updateUser: (u: AuthUser) => void
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoggedIn: false,
  login: () => {},
  logout: () => {},
  updateUser: () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(getStoredUser)
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated)

  useEffect(() => {
    setUser(getStoredUser())
    setIsLoggedIn(isAuthenticated())
  }, [])

  const login = useCallback((data: TokenResponse) => {
    saveTokens(data)
    setUser(data.user)
    setIsLoggedIn(true)
  }, [])

  const logout = useCallback(() => {
    clearTokens()
    setUser(null)
    setIsLoggedIn(false)
  }, [])

  const updateUser = useCallback((u: AuthUser) => {
    setUser(u)
    localStorage.setItem('auth_user', JSON.stringify(u))
  }, [])

  return (
    <AuthContext.Provider value={{ user, isLoggedIn, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  return useContext(AuthContext)
}
